// What this module needs from the app, typed structurally: nothing here imports the app, so the
// module can move to a package. `src/app/register-database.tsx` builds it from real values and
// hands it to `createDatabaseModule(host)` (module.ts). The HTTP client is not a member: it comes
// from the shared registry, `getHttpClient('serviceApi')` of `@vnetwork/margarita-core/http`.

/** The services-catalogue entry (`AvailableService` of `src/api/services.ts`), mirrored
 *  structurally: the host functions below take the app's own type, so this must be as wide as it. */
export interface DatabaseCatalogService {
  id: string;
  service_name: string;
  organization_id: string;
  provider_id: string;
  provider?: {
    id: string;
    name: string;
    display_name: string;
    type: string;
    legacy_scope?: string;
  };
  status: string;
  zones_count: number;
  created_at: string;
  updated_at?: string;
}

export interface DatabaseHost {
  /** The user's provisioned services (`useMyServices` of the app's services catalogue). */
  useMyServices: () => { data?: DatabaseCatalogService[]; isLoading: boolean; isError: boolean };
  /** The display name of a catalogue entry (`serviceLabel`). */
  serviceLabel: (service: DatabaseCatalogService) => string;
  /** The provider scope of a catalogue entry (`serviceProviderScope`), matched against
   *  `DATABASE_SCOPE` to pick this module's services. */
  serviceProviderScope: (service: DatabaseCatalogService) => string;
}

let current: DatabaseHost | null = null;

/** Called once by `createDatabaseModule`, before any route renders. */
export function registerDatabaseHost(host: DatabaseHost): void {
  current = host;
}

/** The registered host. Throws when the module is used without `createDatabaseModule(host)`. */
function databaseHost(): DatabaseHost {
  if (!current) throw new Error('Database: createDatabaseModule(host) was not called before the module rendered');
  return current;
}

// Resolved at call time so call sites read like the shared ones.
export const useMyServices: DatabaseHost['useMyServices'] = () => databaseHost().useMyServices();
export const serviceLabel: DatabaseHost['serviceLabel'] = (service) => databaseHost().serviceLabel(service);
export const serviceProviderScope: DatabaseHost['serviceProviderScope'] = (service) =>
  databaseHost().serviceProviderScope(service);
